/* eslint-disable no-console */
import axios from 'axios';
import { format, isWithinInterval, parseISO } from '@@/js/utils/dateFns';

const owemInstance = axios.create({
    baseURL: process.env.RTC_OWEM_API_ENDPOINT,
    timeout: 10000,
});
const gtfsInstance = axios.create({
    baseURL: process.env.RTC_GTFS_API_ENDPOINT,
    timeout: 10000,
});

[owemInstance, gtfsInstance].forEach((instance) => {
    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            const config = error.config || {};

            if (error.response) {
                if (!config.continueOnFailure) {
                    console.error(`[API ${error.response.status}] ${error.response.statusText}`);
                    throw error;
                }
            } else if (['ECONNABORTED', 'ENOTFOUND'].includes(error.code) || error.message === 'Network Error') {
                if (!config.continueOnFailure) {
                    console.error('[API 503] Service indisponible');
                    throw error;
                } else if (config.showWarningOnFailure) {
                    // @TODO: Reconduire le message d'erreur vers le store pour
                    // l'affichage dans le header
                    // ctx.app.store.commit('SET_SITE_WARNING', {
                    //     code: 'api_unavail',
                    // });
                    console.warn('[API warning] Service indisponible');
                }
            } else if (config && !config.continueOnFailure) {
                console.error('[API error] Erreur serveur', error.message || error);
                throw error;
            }

            throw error;
        },
    );
});

export default {
    /**
     * Instance de axios pour le service OWEM.
     * @type {Object}
     */
    axiosOwem: owemInstance,
    /**
     * Instance de axios pour le service GTFS.
     * @type {Object}
     */
    axiosGTFS: gtfsInstance,
    /**
     * Fonction wrapper permettant de retourner l'objet de data de axios directement.
     * Utile dans le cas du asyncData.
     * @param  {Promise} request Axios Request
     * @return {Promise} Promise qui resolve lorsque la Promise Axios resolve contenant
     * le data du retour axios.
     */
    throughData(request) {
        return new Promise((resolve, reject) => {
            request
                .then((res) => {
                    resolve(res ? res.data : null);
                })
                .catch(reject);
        });
    },
    /**
     * Retourne les informations de tous les parcours ou un parcours en
     * particulier si noParcours est passé en paramètre
     * ListeParcours
     * @param  {string|int} noParcours (optionel) Numéro du parcours à retourner
     * @param  {Boolean} allowFailure (optionel) Permettre à la requête de faire une erreur
     * sans afficher la page d'erreur.
     * @return {Promise(array|object)} Liste de parcours ou parcours selon le noParcours
     */
    getRouteList(allowFailure) {
        const request = this.axiosGTFS.get('/legacy/ListeParcours', {
            continueOnFailure: !!allowFailure,
            showWarningOnFailure: !!allowFailure,
        });
        return this.throughData(request);
    },
    getRoute(noRoute, allowFailure, date = new Date()) {
        let request = {};
        if (date !== null) {
            request = this.axiosGTFS.get('/legacy/Parcours_Periode', {
                params: {
                    noParcours: noRoute,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
                continueOnFailure: !!allowFailure,
                showWarningOnFailure: !!allowFailure,
            });
        } else {
            request = this.axiosGTFS.get('/legacy/Parcours', {
                params: {
                    noParcours: noRoute,
                },
                continueOnFailure: !!allowFailure,
                showWarningOnFailure: !!allowFailure,
            });
        }
        return this.throughData(request);
    },
    async getApplicationPeriods() {
        const request = this.axiosOwem.get('services/iv/siteweb/v1/planification/periodes-application');
        const periods = await this.throughData(request);
        return periods.sort((a) => {
            return !isWithinInterval(new Date(), a.dateDebutPeriode, a.dateFinPeriode) ? 1 : -1;
        });
    },
    /**
     * Retourne les informations du tracé pour un parcours donné
     * @param  {string|int} noRoute          Numéro du parcours
     * @param  {string|int} direction        (optionnel) Code de direction
     * @param  {string|int} routeType        (optionnel) Type de parcours
     * @return {Promise(object|array)}       Informations de(s) tracé(s)
     */
    getParcoursTrace(noRoute, direction, routeType, date = new Date()) {
        if (noRoute) {
            const request = this.axiosGTFS.get('/legacy/ListeParcoursTypeTrace_ParcoursPeriode', {
                params: {
                    noParcours: noRoute,
                    codeDirection: direction || 0,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return new Promise((resolve, reject) => {
                request
                    .then((res) => {
                        if (routeType === 'all') {
                            resolve(res.data);
                        } else if (routeType) {
                            resolve(res.data.filter((t) => t.idParcoursType === routeType));
                        } else {
                            const routeLine = res.data.pop();
                            resolve(routeLine);
                        }
                    })
                    .catch(reject);
            });
        }
        return {
            error: 'noRoute is required',
        };
    },
    /**
     * Retourne un arrêt qu'il est permis de consulté (entre aujourd’hui et +14 jours)
     * Arret
     * @param  {string|int}       noStop   Numéro de l'arrêt
     * @return {Promise(array)}           Informations de l'arrêt
     */
    getStop(noStop) {
        if (noStop) {
            const request = this.axiosGTFS.get('/legacy/Arret', {
                params: {
                    noArret: noStop,
                },
            });
            return this.throughData(request);
        }
        return {
            error: 'noStop is required',
        };
    },
    /**
     * Retourne les arrêts d'un parcours dans la direction donnée
     * ListeArret_Parcours
     * @param  {string|int} noRoute       Numéro du parcours
     * @param  {string|int} direction (optionnel) Code de direction (normalement 0 ou 1)
     * @param  {string|int|date} date     (optionnel) Date des arrêts
     * @return {Promise(array)}           Liste des arrêts
     */
    getStops(noRoute, direction, date, period) {
        const url = period ? 'ListeArret_ParcoursPeriode' : 'ListeArret_Parcours';
        if (noRoute) {
            const request = this.axiosGTFS.get(`legacy/${url}`, {
                params: {
                    noParcours: noRoute,
                    codeDirection: direction || 0,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return this.throughData(request);
        }
        return {
            error: 'noRoute is required',
        };
    },
    /**
     * Retourne la liste des parcours de correspondance pour un arrêt/parcours/direction/date
     * passé en paramètres.
     * ListeParcoursCorrespondance_ArretParcours
     * @param  {string|int} noStop        Numéro d'arrêt
     * @param  {string|int} noRoute       Numéro du parcours
     * @param  {string|int} direction     (optionnel) Code de direction (normalement 0 ou 1)
     * @param  {string|int|date} date     (optionnel) Date des arrêts
     * @return {Promise(array)}           Liste des parcours
     */
    getRouteTransfers(noStop, noRoute, direction, date) {
        if (noRoute) {
            const request = this.axiosGTFS.get('/legacy/ListeParcoursCorrespondance_ArretParcours', {
                params: {
                    noArret: noStop,
                    noParcours: noRoute,
                    codeDirection: direction || 0,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return this.throughData(request);
        }
        return {
            error: 'noRoute is required',
        };
    },
    /**
     * Retourne une liste de borne virtuelle pour un arrêt et une date passé en paramètres.
     * Les bornes retournées seront celles qui sont active pour la date passée en paramètre.
     * Les horaires à venir seront fournis seulement pour la journée en cours.
     * ListeBorneVirtuelle_Arret
     * @param  {string|int}      noStop  Numéro de l'arrêt
     * @param  {string|int|date} date     (optionnel) Date du jour
     * @return {Promise(array)}           Liste des bornes virtuelles
     */
    getStopDepartures(noStop, date) {
        if (noStop && date) {
            const request = this.axiosGTFS.get('/legacy/ListeBorneVirtuelle_Arret', {
                continueOnFailure: true,
                params: {
                    noArret: noStop,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return this.throughData(request);
        }
        const emptyFields = [];
        if (!noStop) emptyFields.push('noStop');
        if (!date) emptyFields.push('date');
        return {
            error: `${emptyFields.join(', ')} ${emptyFields.length > 1 ? 'are' : 'is'} required`,
        };
    },
    /**
     * Retourne une liste de borne virtuelle pour des arrêts et une date passé en paramètres.
     * Les bornes retournées seront celles qui sont active pour la date passée en paramètre.
     * Les horaires à venir seront fournis seulement pour la journée en cours.
     * ListeBorneVirtuelle_Arrets
     * @param  {array}          stops    Array d'arrets
     * @param  {string|int|date} date     (optionnel) Date du jour
     * @return {Promise(array)}           Liste des bornes virtuelles
     */
    getStopsDepartures(stops, date) {
        if (stops && date) {
            const request = this.axiosGTFS.get('/legacy/ListeBorneVirtuelle_Arrets', {
                continueOnFailure: true,
                params: {
                    arrets: stops.join(','),
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return this.throughData(request);
        }
        const emptyFields = [];
        if (!stops) emptyFields.push('stops');
        if (!date) emptyFields.push('date');
        return {
            error: `${emptyFields.join(', ')}${emptyFields.length > 1 ? ' are' : ' is'} required`,
        };
    },
    /**
     * Retourne les passages planifiés pour un parcours à un arrêt dans une direction pour une date
     * ListeHoraire_ArretParcours
     * @param  {string|int} noRoute       Numéro du parcours
     * @param  {string|int} noStop       Numéro de l'arrêt
     * @param  {string|int} direction (optionnel) Code de direction (normalement 0 ou 1)
     * @param  {string|int|date} date     (optionnel) Date des passages
     * @return {Promise(array)}           Liste de passages
     */
    getDepartures(noRoute, noStop, direction, date) {
        if (noRoute && noStop) {
            const request = this.axiosGTFS.get('/legacy/ListeHoraire_ArretParcours', {
                params: {
                    noParcours: noRoute,
                    noArret: noStop,
                    codeDirection: direction || 0,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return this.throughData(request);
        }
        const emptyFields = [];
        if (!noRoute) emptyFields.push('noRoute');
        if (!noStop) emptyFields.push('noStop');
        return {
            error: `${emptyFields.join(', ')}${emptyFields.length > 1 ? ' are' : ' is'} required`,
        };
    },
    /**
     * Retourne les temps de passage pour chaque arrêt d'un voyage
     * @param  {string|int} idDeparture Indentifiant du voyage
     * @param  {string|int|dat} date    (optionnel) Date des passsages
     * @return {Promise(array)}         Liste des passages
     */
    getTripDepartures(idDeparture, date) {
        if (idDeparture) {
            const request = this.axiosGTFS.get('/legacy/ListeHoraire_Voyage', {
                params: {
                    idVoyage: idDeparture,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return this.throughData(request);
        }
        return {
            error: 'idDeparture is required',
        };
    },
    /**
     *  Retourne une borne virtuelle pour un arrêt/parcours/direction/date passé
     *  en paramètres. La borne virtuelle sera retourné si elle est inclus dans
     *  la période qu'il est permis de consulté. Les horaires à venir seront
     *  fournis seulement pour la journée en cours.
     * @param  {string|int} noRoute        Numéro du parcours
     * @param  {string|int} noStop         Numéro de l'arrêt
     * @param  {string|int} direction      Code de direction
     * @param  {string|int|dat} date       (optionnel) Date consultée
     * @return {Promise(array)}            Les 3 prochains passages
     */
    getNextDepartures(noRoute, noStop, direction, date) {
        if (noRoute && noStop) {
            const request = this.axiosGTFS.get('/legacy/BorneVirtuelle_ArretParcours', {
                continueOnFailure: true,
                params: {
                    noParcours: noRoute,
                    noArret: noStop,
                    codeDirection: direction || 0,
                    date: format(typeof date === 'string' ? parseISO(date) : date, 'yyyyMMdd'),
                },
            });
            return this.throughData(request);
        }
        const emptyFields = [];
        if (!noRoute) emptyFields.push('noRoute');
        if (!noStop) emptyFields.push('noStop');
        return {
            error: `${emptyFields.join(', ')}${emptyFields.length > 1 ? ' are' : ' is'} required`,
        };
    },
    /**
     * Retourne une liste de positions des autobus pour un parcours et dans une direction
     * ListeAutobus_Parcours
     * @param  {string|int} noParcours    Numéro du parcours
     * @param  {string|int} codeDirection Code de direction
     * @return {Promise(array)}           Liste des positions des autobus
     */
    getBusPositions(noParcours, codeDirection) {
        if (noParcours) {
            const request = this.axiosGTFS.get('/legacy/ListeAutobus_Parcours', {
                continueOnFailure: true,
                params: {
                    noParcours,
                    codeDirection: codeDirection || 0,
                },
            });
            return this.throughData(request);
        }
        return {
            error: 'noParcours is required',
        };
    },
    /**
     * Retourne les temps de passage aux arrêts d'un autobus pour un voyage
     * ListeHoraire_Autobus
     * @param  {string|int} idAutobus Indentifiant d'autobus (provenant de busPositions)
     * @param  {string|int} idVoyage  Indentifiant du voyage (provenant de busPositions)
     * @return {Promise(array)}       Liste des temps de passages aux arrêts
     */
    getBusPassages(idAutobus, idVoyage) {
        if (idAutobus && idVoyage) {
            const request = this.axiosGTFS.get('/legacy/ListeHoraire_Autobus', {
                params: {
                    idAutobus,
                    idVoyage,
                },
            });
            return this.throughData(request);
        }
        const emptyFields = [];
        if (!idAutobus) emptyFields.push('idAutobus');
        if (!idVoyage) emptyFields.push('idVoyage');
        return {
            error: `${emptyFields.join(', ')}${emptyFields.length > 1 ? ' are' : ' is'} required`,
        };
    },
    getParcOBus() {
        const request = this.axiosOwem.get('services/iv/appmobile/v1/parc-o-bus');
        return this.throughData(request);
    },
};
